import { useEffect, useMemo, useRef } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { CUT, getFixingSpec, DEFAULT_FIXING_ID } from "@/config/designer";
import { snapNdcLine } from "@/core/cut/angleSnap";
import type { DesignerEngine, SurfaceHit } from "@/core/engine/DesignerEngine";
import type { BrushKind } from "@/core/field/brushes";
import { isCuttingTool, useDesignerStore } from "@/state/designerStore";
import type { BrushUniforms } from "./holdMaterial";
import {
  playActionSound,
  startSculptSound,
  stopSculptSound,
  updateSculptSound,
} from "@/audio/carvingSound";

interface InteractionProps {
  engine: DesignerEngine;
  uniforms: BrushUniforms;
  /**
   * Filled in with a function that turns the drawn line into a cut. The
   * conversion needs the live camera, which only exists inside the canvas, so
   * the toolbar reaches in through this handle rather than the other way round.
   */
  cutRunner: React.MutableRefObject<(() => void) | null>;
}

type Mode = "idle" | "sculpting" | "paintingGloss" | "movingFixing" | "drawingCut";

const MOSS = new THREE.Color("#c8d7bc");
const BEIGE = new THREE.Color("#c3b89f");
const RUST = new THREE.Color("#b4705f");
const GLOSS = new THREE.Color("#f2f1e9");
const Y_AXIS = new THREE.Vector3(0, 1, 0);
const Z_AXIS = new THREE.Vector3(0, 0, 1);
const fixingAxis = new THREE.Vector3();

/**
 * All pointer handling for the viewport.
 *
 * Listeners are attached in the CAPTURE phase on the canvas's parent, which is
 * what lets a left-drag mean two different things without the two fighting:
 * if the pointer is over the hold and a sculpting tool is active we take the
 * drag and switch the orbit controls off for its duration; otherwise the event
 * carries on to OrbitControls untouched. Camera movement follows Blender:
 * middle-drag orbits, Shift+middle-drag pans, and the wheel zooms. The wheel
 * only changes brush size mid-stroke or with Ctrl held.
 */
export function Interaction({ engine, uniforms, cutRunner }: InteractionProps): React.ReactElement {
  const gl = useThree((state) => state.gl);
  const camera = useThree((state) => state.camera);
  const controls = useThree((state) => state.controls) as OrbitControlsImpl | null;

  const mode = useRef<Mode>("idle");
  const strokeKind = useRef<BrushKind>("carve");
  const movingId = useRef<string | null>(null);
  const movingStart = useRef<{ x: number; z: number } | null>(null);
  /** True while this component owns the drag, so the camera is always handed back. */
  const owned = useRef(false);
  const shiftHeld = useRef(false);
  const angleStart = useRef<[number, number] | null>(null);
  const lastAngleTone = useRef<number | null>(null);

  const raycaster = useMemo(() => new THREE.Raycaster(), []);
  const ndc = useMemo(() => new THREE.Vector2(), []);

  const ghost = useRef<THREE.Group>(null);
  const ghostRing = useRef<THREE.Mesh>(null);
  const ghostShaft = useRef<THREE.Mesh>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      shiftHeld.current = event.shiftKey;
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKey);
    };
  }, []);

  // Turn the drawn line into a prismatic sweep along the view direction.
  useEffect(() => {
    cutRunner.current = () => {
      const state = useDesignerStore.getState();
      if (state.cutPoints.length < 2) {
        state.notify("Draw a line across the hold first.", "warn");
        return;
      }
      if (!(camera instanceof THREE.OrthographicCamera)) {
        state.notify("Pick a Top, Front, Back, Left or Right view to cut from.", "warn");
        return;
      }

      camera.updateMatrixWorld();
      const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).normalize();
      const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1).normalize();
      const origin = new THREE.Vector3(
        engine.preset.width / 2,
        engine.preset.height / 2,
        engine.preset.depth / 2,
      );

      const points = state.cutPoints.map(([nx, ny]) => {
        const world = new THREE.Vector3(nx, ny, 0).unproject(camera).sub(origin);
        return [world.dot(right), world.dot(up)] as [number, number];
      });

      state.setCutting(true);
      // Let the busy state paint before the field work blocks the thread.
      window.setTimeout(() => {
        try {
          const result = engine.cut({
            origin: [origin.x, origin.y, origin.z],
            right: [right.x, right.y, right.z],
            up: [up.x, up.y, up.z],
            points,
          });
          if (result.applied) {
            const angleLabel = state.tool === "angle" ? state.cutAngleDegrees : null;
            if (state.soundEnabled) playActionSound(state.tool === "angle" ? "angle" : "cut");
            state.clearCutPoints();
            state.notify(
              result.discardedVoxels > 0
                ? `${angleLabel === null ? "Cut" : `${angleLabel}° angle cut`} through. The offcut has been removed.`
                : `${angleLabel === null ? "Cut" : `${angleLabel}° angle cut`} through.`,
            );
          } else {
            state.notify(result.reason ?? "That cut did nothing.", "warn");
          }
        } catch (error) {
          state.notify(
            `The cut failed: ${error instanceof Error ? error.message : String(error)}`,
            "warn",
          );
        } finally {
          useDesignerStore.getState().setCutting(false);
        }
      }, 30);
    };
    return () => {
      cutRunner.current = null;
    };
  }, [camera, engine, cutRunner]);

  useEffect(() => {
    const canvas = gl.domElement;
    const host = canvas.parentElement ?? canvas;

    // setPointerCapture throws if the pointer is no longer active; a failed
    // capture must never abort a stroke.
    const capture = (pointerId: number): void => {
      try {
        canvas.setPointerCapture(pointerId);
      } catch {
        /* the pointer went away; the stroke still ends on pointerup */
      }
    };
    const release = (pointerId: number): void => {
      try {
        if (canvas.hasPointerCapture(pointerId)) canvas.releasePointerCapture(pointerId);
      } catch {
        /* nothing to release */
      }
    };

    const pick = (event: PointerEvent): SurfaceHit | null => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      ndc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      return engine.raycast(raycaster.ray.origin, raycaster.ray.direction);
    };

    const currentKind = (): BrushKind => {
      const tool = useDesignerStore.getState().tool;
      if (tool === "smooth") return "smooth";
      return shiftHeld.current ? "smooth" : "carve";
    };

    const showBrush = (hit: SurfaceHit | null): void => {
      const { tool, brushRadius } = useDesignerStore.getState();
      const brushTool = tool === "carve" || tool === "smooth" || tool === "texture";
      if (!brushTool || !hit) {
        uniforms.uBrushActive.value = 0;
        return;
      }
      uniforms.uBrushActive.value = 1;
      uniforms.uBrushRadius.value = brushRadius;
      uniforms.uBrushPos.value.set(hit.point[0], hit.point[1], hit.point[2]);
      uniforms.uBrushTint.value.copy(
        tool === "texture" ? GLOSS : currentKind() === "smooth" ? BEIGE : MOSS,
      );
    };

    const updateGhost = (hit: SurfaceHit | null): void => {
      const group = ghost.current;
      if (!group) return;
      const { tool } = useDesignerStore.getState();
      if (tool !== "fixings" || !hit || mode.current === "sculpting") {
        group.visible = false;
        return;
      }
      const spec = getFixingSpec(DEFAULT_FIXING_ID);
      const x = hit.point[0];
      const z = hit.point[2];
      const check = engine.checkFixingPlacement(x, z);
      const surface = check.surfacePoint;
      const rear = check.rearPoint;
      if (!surface || !rear) {
        group.visible = false;
        return;
      }
      fixingAxis.fromArray(check.axis);

      group.visible = true;
      group.position.set(0, 0, 0);
      if (ghostRing.current) {
        ghostRing.current.position.set(
          surface[0] + check.axis[0] * 0.4,
          surface[1] + check.axis[1] * 0.4,
          surface[2] + check.axis[2] * 0.4,
        );
        ghostRing.current.quaternion.setFromUnitVectors(Z_AXIS, fixingAxis);
        applyGhostColour(ghostRing.current, check.valid);
      }
      if (ghostShaft.current) {
        const length = Math.max(1, Math.hypot(surface[0] - rear[0], surface[1], surface[2] - rear[2]));
        ghostShaft.current.scale.y = length;
        ghostShaft.current.position.set(
          (surface[0] + rear[0]) / 2,
          surface[1] / 2,
          (surface[2] + rear[2]) / 2,
        );
        ghostShaft.current.quaternion.setFromUnitVectors(Y_AXIS, fixingAxis);
        applyGhostColour(ghostShaft.current, check.valid);
      }
      group.userData.radius = spec.countersinkDiameter / 2;
    };

    const toNdc = (event: PointerEvent): [number, number] | null => {
      const rect = canvas.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return null;
      return [
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      ];
    };

    const onPointerDown = (event: PointerEvent): void => {
      if (event.button !== 0) return;
      const state = useDesignerStore.getState();
      if (state.navigationMode !== "sculpt") return;

      if (isCuttingTool(state.tool)) {
        // The cut line may start off the hold, so this runs before any pick.
        const point = toNdc(event);
        if (!point) return;
        event.stopPropagation();
        event.preventDefault();
        mode.current = "drawingCut";
        owned.current = true;
        if (controls) controls.enabled = false;
        capture(event.pointerId);
        state.clearCutPoints();
        if (state.tool === "angle") {
          angleStart.current = point;
          lastAngleTone.current = null;
        }
        else state.addCutPoint(point);
        return;
      }

      const hit = pick(event);
      if (!hit) {
        if (state.tool === "fixings") state.selectFixing(null);
        return; // let OrbitControls orbit from empty space
      }

      if (state.tool === "texture") {
        event.stopPropagation();
        event.preventDefault();
        mode.current = "paintingGloss";
        owned.current = true;
        if (controls) controls.enabled = false;
        capture(event.pointerId);
        engine.beginGlossStroke();
        engine.paintGloss(hit, state.brushRadius);
        showBrush(hit);
        return;
      }

      if (state.tool === "fixings") {
        event.stopPropagation();
        event.preventDefault();
        const spec = getFixingSpec(DEFAULT_FIXING_ID);
        const x = hit.point[0];
        const z = hit.point[2];
        const existing = engine.fixingNear(x, z, spec.countersinkDiameter / 2 + 4);
        const target = existing ?? engine.addFixing(x, z);

        if (target) {
          if (!existing && state.soundEnabled) playActionSound("fixing");
          state.selectFixing(target.id);
          // Placing and moving are the same gesture: a click drops the hole, and
          // keeping the button down slides it into place.
          movingId.current = target.id;
          movingStart.current = { x: target.x, z: target.z };
          mode.current = "movingFixing";
        } else {
          const check = engine.checkFixingPlacement(x, z);
          state.notify(check.reason ?? "A fixing will not fit there.", "warn");
        }

        owned.current = true;
        if (controls) controls.enabled = false;
        capture(event.pointerId);
        return;
      }

      // Utility tools such as Flange use their panel action rather than a
      // viewport stroke. Empty-space drags remain available to the camera.
      if (state.tool !== "carve" && state.tool !== "smooth") return;

      event.stopPropagation();
      event.preventDefault();
      mode.current = "sculpting";
      strokeKind.current = currentKind();
      owned.current = true;
      if (controls) controls.enabled = false;
      capture(event.pointerId);

      engine.beginStroke();
      engine.strokeTo(
        hit,
        strokeKind.current,
        state.brushRadius,
        strokeKind.current === "smooth" ? state.smoothStrength : state.carveStrength,
        state.baseLocked ? state.baseThickness : 0,
      );
      if (state.soundEnabled) {
        const strength = strokeKind.current === "smooth" ? state.smoothStrength : state.carveStrength;
        startSculptSound(strokeKind.current, state.brushRadius, strength);
      }
      showBrush(hit);
    };

    const onPointerMove = (event: PointerEvent): void => {
      const state = useDesignerStore.getState();

      if (state.navigationMode !== "sculpt" && mode.current === "idle") {
        uniforms.uBrushActive.value = 0;
        if (ghost.current) ghost.current.visible = false;
        return;
      }

      if (mode.current === "drawingCut") {
        event.stopPropagation();
        const point = toNdc(event);
        if (point && state.tool === "angle" && angleStart.current) {
          const rect = canvas.getBoundingClientRect();
          const snapped = snapNdcLine(
            angleStart.current,
            point,
            rect.width,
            rect.height,
            CUT.angleStepDegrees,
          );
          const length = Math.hypot(
            snapped.end[0] - angleStart.current[0],
            snapped.end[1] - angleStart.current[1],
          );
          if (length >= CUT.minPointSpacingNdc) {
            state.setAngleCut(angleStart.current, snapped.end, snapped.degrees);
            if (state.soundEnabled && snapped.degrees !== lastAngleTone.current) {
              playActionSound("angle");
              lastAngleTone.current = snapped.degrees;
            }
          } else {
            state.clearCutPoints();
          }
        } else if (point) {
          state.addCutPoint(point);
        }
        return;
      }

      if (mode.current === "sculpting") {
        event.stopPropagation();
        const hit = pick(event);
        if (hit) {
          engine.strokeTo(
            hit,
            strokeKind.current,
            state.brushRadius,
            strokeKind.current === "smooth" ? state.smoothStrength : state.carveStrength,
            state.baseLocked ? state.baseThickness : 0,
          );
          if (state.soundEnabled) {
            const strength = strokeKind.current === "smooth" ? state.smoothStrength : state.carveStrength;
            updateSculptSound(strokeKind.current, state.brushRadius, strength);
          }
          showBrush(hit);
        }
        return;
      }

      if (mode.current === "paintingGloss") {
        event.stopPropagation();
        const hit = pick(event);
        if (hit) {
          engine.paintGloss(hit, state.brushRadius);
          showBrush(hit);
        }
        return;
      }

      if (mode.current === "movingFixing" && movingId.current) {
        event.stopPropagation();
        const hit = pick(event);
        if (hit) engine.previewMoveFixing(movingId.current, hit.point[0], hit.point[2]);
        return;
      }

      const hit = pick(event);
      showBrush(hit);
      updateGhost(hit);
    };

    const finish = (event: PointerEvent): void => {
      if (mode.current === "sculpting") {
        engine.endStroke(strokeKind.current);
        stopSculptSound();
      } else if (mode.current === "paintingGloss") {
        engine.endGlossStroke();
      } else if (mode.current === "movingFixing" && movingId.current && movingStart.current) {
        const fixing = engine.fixings.find((f) => f.id === movingId.current);
        const start = movingStart.current;
        if (fixing && (fixing.x !== start.x || fixing.z !== start.z)) {
          const { x, z } = fixing;
          engine.previewMoveFixing(fixing.id, start.x, start.z);
          engine.moveFixing(fixing.id, x, z);
        }
      }
      // Always hand the camera back, even when the gesture did nothing —
      // leaving the controls disabled would silently kill orbiting.
      if (owned.current) {
        owned.current = false;
        if (controls) controls.enabled = true;
        release(event.pointerId);
      }
      mode.current = "idle";
      movingId.current = null;
      movingStart.current = null;
      angleStart.current = null;
    };

    const onPointerLeave = (): void => {
      if (mode.current === "idle") {
        uniforms.uBrushActive.value = 0;
        if (ghost.current) ghost.current.visible = false;
      }
    };

    const onWheel = (event: WheelEvent): void => {
      // Mid-stroke, or with Ctrl/Cmd held, the wheel resizes the brush.
      // Everywhere else it stays the zoom control users expect.
      const resizing =
        mode.current === "sculpting" ||
        mode.current === "paintingGloss" ||
        event.ctrlKey ||
        event.metaKey;
      if (!resizing) return;
      const state = useDesignerStore.getState();
      if (state.tool === "fixings" && mode.current !== "sculpting") return;
      event.preventDefault();
      event.stopPropagation();
      state.scaleBrushRadius(event.deltaY < 0 ? 1.1 : 1 / 1.1);
      uniforms.uBrushRadius.value = useDesignerStore.getState().brushRadius;
    };

    const onContextMenu = (event: MouseEvent): void => event.preventDefault();

    host.addEventListener("pointerdown", onPointerDown, { capture: true });
    host.addEventListener("pointermove", onPointerMove, { capture: true });
    host.addEventListener("pointerup", finish, { capture: true });
    host.addEventListener("pointercancel", finish, { capture: true });
    host.addEventListener("pointerleave", onPointerLeave, { capture: true });
    host.addEventListener("wheel", onWheel, { capture: true, passive: false });
    host.addEventListener("contextmenu", onContextMenu);

    return () => {
      host.removeEventListener("pointerdown", onPointerDown, { capture: true });
      host.removeEventListener("pointermove", onPointerMove, { capture: true });
      host.removeEventListener("pointerup", finish, { capture: true });
      host.removeEventListener("pointercancel", finish, { capture: true });
      host.removeEventListener("pointerleave", onPointerLeave, { capture: true });
      host.removeEventListener("wheel", onWheel, { capture: true });
      host.removeEventListener("contextmenu", onContextMenu);
      if (controls) controls.enabled = true;
      stopSculptSound();
    };
  }, [engine, uniforms, gl, camera, controls, raycaster, ndc, movingStart]);

  const spec = getFixingSpec(DEFAULT_FIXING_ID);

  return (
    <group ref={ghost} visible={false}>
      <mesh ref={ghostRing}>
        <ringGeometry args={[spec.countersinkDiameter / 2 - 0.7, spec.countersinkDiameter / 2 + 0.7, 48]} />
        <meshBasicMaterial color={MOSS} transparent opacity={0.95} side={THREE.DoubleSide} depthTest={false} />
      </mesh>
      <mesh ref={ghostShaft}>
        <cylinderGeometry args={[spec.shaftDiameter / 2, spec.shaftDiameter / 2, 1, 24, 1, true]} />
        <meshBasicMaterial
          color={MOSS}
          transparent
          opacity={0.35}
          side={THREE.DoubleSide}
          depthTest={false}
        />
      </mesh>
    </group>
  );
}


function applyGhostColour(mesh: THREE.Mesh, valid: boolean): void {
  const material = mesh.material as THREE.MeshBasicMaterial;
  material.color.copy(valid ? MOSS : RUST);
}
